import React, { useState, useMemo, useEffect } from 'react';
import { FormControl, Select, MenuItem, Checkbox, ListItemText, Button, TextField } from '@mui/material';
import SentimentAnalysisChart from './SentimentAnalysisChart';
import { PREMIUM_MODES } from '../utils/stockOverlay';
import ProductSelector from './ProductSelector';
import { apiPost } from '../services/apiClient';

const DEFAULT_SOURCE_TYPES = [
    { value: 'all', label: 'All Sources' }
];

const TIME_RANGE_OPTIONS = [
    { value: 1, label: '4 Weeks' },
    { value: 3, label: '3 Months' },
    { value: 6, label: '6 Months' },
    { value: 11, label: '11 Months' },
    { value: 12, label: '12 Months' },
    { value: 14, label: '14 Months' },
    { value: 22, label: '22 Months' },
    { value: 24, label: '24 Months' },
    { value: 30, label: '30 Months' },
    { value: 36, label: '36 Months' },
    { value: 48, label: '48 Months' },
    { value: 60, label: '60 Months' },
];

const CustomSentimentBreakdown = ({ topicMap, companyName, companyTicker,
    // Optional shared product selection: adopted into the pending (not
    // applied) product dropdown when valid here, so the shared topic is
    // pre-selected but the user still clicks Apply, matching this
    // component's existing behavior. Selections applied here are pushed
    // back up so other sections of the page can pick them up.
    selectedProduct: sharedSelectedProduct, onProductChange }) => {
    // Selected = UI state (pending), Applied = what the chart uses
    const [selectedType, setSelectedType] = useState('NPS');
    // Overlay mode: 'off' | 'price' | 'peers' | 'snp500' | 'sector'.
    const [overlayMode, setOverlayMode] = useState('off');
    const [appliedType, setAppliedType] = useState('NPS');
    // Lazy-initialize from the shared selection (if valid) so local state
    // is never empty on first render — see PricingPlot.js for why an
    // empty initial value lets ProductSelector's own internal auto-select
    // effect win a race and pre-fill the pending dropdown with its own
    // default instead of the incoming shared topic.
    const [selectedProduct, setSelectedProduct] = useState(() =>
        (sharedSelectedProduct && topicMap?.[sharedSelectedProduct]) ? sharedSelectedProduct : null
    );
    const [appliedProduct, setAppliedProduct] = useState(null);
    const [availableTypes, setAvailableTypes] = useState([
        { value: 'NPS', label: 'Net Promoter Score' },
        { value: 'pricing', label: 'Pricing Sentiment' }
    ]);

    // Source and time range state
    const [availableSources, setAvailableSources] = useState(DEFAULT_SOURCE_TYPES);
    const [selectedSources, setSelectedSources] = useState(['all']);
    const [appliedSources, setAppliedSources] = useState(['all']);
    const [timeRange, setTimeRange] = useState(12);
    const [appliedTimeRange, setAppliedTimeRange] = useState(12);
    const [selectedEndDate, setSelectedEndDate] = useState('');
    const [appliedEndDate, setAppliedEndDate] = useState('');

    // Competitor comparison state (selected = topic ids, applied = {id, name})
    const [selectedCompetitors, setSelectedCompetitors] = useState([]);
    const [appliedCompetitors, setAppliedCompetitors] = useState([]);

    // Hover state for header card
    const [isHeaderHovered, setIsHeaderHovered] = useState(false);

    // Dirty filter indicator: compare selected (pending) vs applied state
    const isFiltersDirty = useMemo(() => {
        if (!appliedProduct && !selectedProduct) return false;
        if (selectedProduct !== appliedProduct) return true;
        if (selectedType !== appliedType) return true;
        if (timeRange !== appliedTimeRange) return true;
        if (selectedEndDate !== appliedEndDate) return true;
        if (JSON.stringify(selectedSources) !== JSON.stringify(appliedSources)) return true;
        if (JSON.stringify(selectedCompetitors) !== JSON.stringify(appliedCompetitors.map(c => c.id))) return true;
        return false;
    }, [selectedProduct, appliedProduct, selectedType, appliedType, timeRange, appliedTimeRange, selectedEndDate, appliedEndDate, selectedSources, appliedSources, selectedCompetitors, appliedCompetitors]);

    // Filter topicMap to only include product topics and add "All Topics" option
    const filteredTopicMap = useMemo(() => {
        if (!topicMap) return {};

        const filtered = {};

        Object.keys(topicMap).forEach(id => {
            const topic = topicMap[id];
            if (topic) {
                filtered[id] = topic;
            }
        });

        // Add "All Topics" option last with lowest priority
        filtered['all'] = {
            id: 'all',
            name: 'All Topics',
            priority: 9999
        };

        return filtered;
    }, [topicMap]);

    // Pre-select the page-wide shared topic in the (pending) dropdown when
    // valid here. Doesn't auto-apply — matches this component's existing
    // "select then click Apply" behavior.
    useEffect(() => {
        if (sharedSelectedProduct && filteredTopicMap[sharedSelectedProduct] && sharedSelectedProduct !== selectedProduct) {
            setSelectedProduct(sharedSelectedProduct);
        }
    }, [sharedSelectedProduct, filteredTopicMap]);

    // Competitors of the effective topic — the selected product, or the
    // highest-priority topic when "All Topics" is selected (same effective-topic
    // rule the type/source fetches use).
    const competitorOptions = useMemo(() => {
        if (!topicMap) return [];

        let effectiveId = selectedProduct;
        if (!selectedProduct || selectedProduct === 'all') {
            const actualTopics = Object.values(topicMap).filter(t => t.id !== 'all');
            if (actualTopics.length === 0) return [];

            effectiveId = actualTopics.sort((a, b) => {
                const priorityA = a.priority === undefined ? Infinity : a.priority;
                const priorityB = b.priority === undefined ? Infinity : b.priority;
                return priorityA - priorityB;
            })[0].id;
        }

        return (topicMap[effectiveId]?.competitors || [])
            .filter(comp => comp.id)
            .map(comp => ({ id: comp.id, name: comp.topic || comp.alt_name || 'Competitor' }));
    }, [selectedProduct, topicMap]);

    const handleTypeChange = (event) => {
        setSelectedType(event.target.value);
    };

    const handleCompetitorChange = (event) => {
        let value = event.target.value;
        if (typeof value === 'string') {
            value = value.split(',');
        }
        setSelectedCompetitors(value);
    };

    // Handle source multi-select changes
    const handleSourceChange = (event) => {
        let value = event.target.value;
        if (typeof value === 'string') {
            value = value.split(',');
        }
        if (value.includes('all') && value.length > 1) {
            value = value.filter(v => v !== 'all');
        } else if (value.length === 0) {
            value = ['all'];
        }
        setSelectedSources(value);
    };

    const applyFilters = () => {
        setAppliedProduct(selectedProduct);
        if (onProductChange && selectedProduct && selectedProduct !== 'all') onProductChange(selectedProduct);
        setAppliedType(selectedType);
        setAppliedSources(selectedSources);
        setAppliedTimeRange(timeRange);
        setAppliedEndDate(selectedEndDate);
        setAppliedCompetitors(competitorOptions.filter(c => selectedCompetitors.includes(c.id)));
    };

    // Reset selected type and competitors to default when product changes
    useEffect(() => {
        setSelectedType('NPS');
        setSelectedCompetitors([]);
    }, [selectedProduct]);

    const getMetricTitle = (metric) => {
        return metric.split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Fetch available types when selectedProduct changes
    useEffect(() => {
        const fetchAvailableTypes = async () => {
            let topicIdToFetch;

            if (selectedProduct && selectedProduct !== 'all') {
                topicIdToFetch = selectedProduct;
            } else {
                const actualTopics = Object.values(topicMap).filter(t => t.id !== 'all');

                if (actualTopics.length === 0) {
                    setAvailableTypes([
                        { value: 'NPS', label: 'Net Promoter Score' },
                        { value: 'pricing', label: 'Pricing Sentiment' }
                    ]);
                    return;
                }

                const sortedTopics = actualTopics.sort((a, b) => {
                    const priorityA = a.priority === undefined ? Infinity : a.priority;
                    const priorityB = b.priority === undefined ? Infinity : b.priority;
                    return priorityA - priorityB;
                });

                topicIdToFetch = sortedTopics[0].id;
            }

            if (!topicIdToFetch) {
                setAvailableTypes([
                    { value: 'NPS', label: 'Net Promoter Score' },
                    { value: 'pricing', label: 'Pricing Sentiment' }
                ]);
                return;
            }

            try {
                const response = await apiPost('topic/assessments/other-types', { topic_id: topicIdToFetch });

                if (!response.ok) throw new Error(`Backend error: ${response.status}`);

                const json = await response.json();
                const data = json.data ?? json ?? [];

                if (!data || data.length === 0) {
                    setAvailableTypes([
                        { value: 'NPS', label: 'Net Promoter Score' },
                        { value: 'pricing', label: 'Pricing Sentiment' }
                    ]);
                    return;
                }

                const otherTypes = data.map(row => ({
                    value: row.type,
                    label: getMetricTitle(row.type)
                }));

                const allTypes = [
                    { value: 'NPS', label: 'Net Promoter Score' },
                    { value: 'pricing', label: 'Pricing Sentiment' },
                    ...otherTypes
                ];

                setAvailableTypes(allTypes);

                if (!allTypes.some(type => type.value === selectedType)) {
                    setSelectedType('NPS');
                }
            } catch (error) {
                console.error('Error fetching assessment types:', error);
                setAvailableTypes([
                    { value: 'NPS', label: 'Net Promoter Score' },
                    { value: 'pricing', label: 'Pricing Sentiment' }
                ]);
            }
        };

        fetchAvailableTypes();
    }, [selectedProduct, topicMap, selectedType]);

    // Fetch available sources when selectedProduct or selectedType changes
    useEffect(() => {
        const fetchAvailableSources = async () => {
            // If no product selected, reset to default
            if (!selectedProduct) {
                setAvailableSources(DEFAULT_SOURCE_TYPES);
                return;
            }

            let topicIdToFetch;

            if (selectedProduct !== 'all') {
                topicIdToFetch = selectedProduct;
            } else {
                const actualTopics = Object.values(topicMap).filter(t => t.id !== 'all');

                if (actualTopics.length === 0) {
                    setAvailableSources(DEFAULT_SOURCE_TYPES);
                    return;
                }

                const sortedTopics = actualTopics.sort((a, b) => {
                    const priorityA = a.priority === undefined ? Infinity : a.priority;
                    const priorityB = b.priority === undefined ? Infinity : b.priority;
                    return priorityA - priorityB;
                });

                topicIdToFetch = sortedTopics[0].id;
            }

            try {
                // Calculate Unix timestamps
                const endTime = Math.floor(Date.now() / 1000);
                const startDate = new Date();
                startDate.setMonth(startDate.getMonth() - timeRange);
                const startTime = Math.floor(startDate.getTime() / 1000);

                const response = await apiPost('topic/assessments/available-sources', {
                    topic_id: topicIdToFetch,
                    assessment_type: selectedType,
                    start_time: startTime,
                    end_time: endTime
                });

                if (!response.ok) throw new Error(`Backend error: ${response.status}`);

                const json = await response.json();
                const data = json.data ?? json ?? [];

                // Always prepend "All Sources" to the list
                const sourcesWithAll = [
                    { value: 'all', label: 'All Sources' },
                    ...data
                ];

                setAvailableSources(sourcesWithAll);

                // Reset to 'all' when product or type changes
                setSelectedSources(['all']);
            } catch (error) {
                console.error('Error fetching available sources:', error);
                setAvailableSources(DEFAULT_SOURCE_TYPES);
            }
        };

        fetchAvailableSources();
    }, [selectedProduct, selectedType, timeRange, topicMap]);

    const getAppliedProductName = () => {
        if (appliedProduct && topicMap && topicMap[appliedProduct]) {
            return topicMap[appliedProduct].name;
        }
        return 'Product';
    };

    const getAppliedTimeRangeLabel = () => {
        const option = TIME_RANGE_OPTIONS.find(o => o.value === appliedTimeRange);
        return option ? option.label : `${appliedTimeRange} Months`;
    };

    const buildMainTitle = () => {
        return `${getAppliedProductName()} · ${getMetricTitle(appliedType)}`;
    };

    const buildSubline = () => {
        const sourcesLabel = appliedSources.includes('all')
            ? 'All Sources'
            : `${appliedSources.length} Source${appliedSources.length > 1 ? 's' : ''}`;
        const endDateLabel = appliedEndDate
            ? ` • Until ${new Date(appliedEndDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
            : '';
        const competitorsLabel = appliedCompetitors.length > 0
            ? ` • vs ${appliedCompetitors.length <= 2
                ? appliedCompetitors.map(c => c.name).join(', ')
                : `${appliedCompetitors.length} Competitors`}`
            : '';
        return `${getAppliedTimeRangeLabel()} • ${sourcesLabel}${endDateLabel}${competitorsLabel}`;
    };

    if (!topicMap || Object.keys(filteredTopicMap).length === 0) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                No product data available for sentiment analysis
            </div>
        );
    }

    // Consistent control height for all dropdowns and button
    const CONTROL_HEIGHT = 36;
    const CONTROL_GAP = 12;

    const selectStyle = {
        width: '100%',
        height: CONTROL_HEIGHT,
        '& .MuiSelect-select': {
            paddingTop: '6px',
            paddingBottom: '6px',
            paddingRight: '24px !important',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d0d0d0',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#999',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1042c1',
        }
    };

    const filterLabelStyle = {
        fontSize: '9px',
        fontWeight: 600,
        color: '#999',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '3px'
    };

    return (
        <div style={{ padding: '24px', maxWidth: '100%', boxSizing: 'border-box' }}>
            <style>{`
                @keyframes pulseRing {
                    0% { box-shadow: 0 0 0 0 rgba(16, 66, 193, 0.4); }
                    70% { box-shadow: 0 0 0 6px rgba(16, 66, 193, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(16, 66, 193, 0); }
                }
            `}</style>
            {/* Section Label */}
            <div style={{
                fontSize: '10px',
                fontWeight: 600,
                color: '#888',
                textTransform: 'uppercase',
                letterSpacing: '1.5px',
                marginBottom: '12px'
            }}>
                CUSTOM SENTIMENT BREAKDOWN
            </div>

            {/* Header Card */}
            <div
                onMouseEnter={() => setIsHeaderHovered(true)}
                onMouseLeave={() => setIsHeaderHovered(false)}
                style={{
                    backgroundColor: appliedProduct ? '#fafafa' : '#f5f7ff',
                    borderRadius: '8px',
                    padding: '20px 24px 18px',
                    marginBottom: '20px',
                    boxShadow: isHeaderHovered
                        ? '0 8px 24px rgba(0, 0, 0, 0.12)'
                        : '0 1px 3px rgba(0, 0, 0, 0.06)',
                    border: isHeaderHovered
                        ? '1px solid rgba(16, 66, 193, 0.18)'
                        : '1px solid rgba(0, 0, 0, 0.06)',
                    transition: 'box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease',
                    transform: isHeaderHovered ? 'translateY(-1px)' : 'translateY(0)',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                }}
            >
                {/* Title Block */}
                <div style={{ marginBottom: '12px' }}>
                    {/* Company Name - lightest */}
                    <div style={{
                        fontSize: '11px',
                        color: '#777',
                        fontWeight: 500,
                        lineHeight: 1.2,
                        marginBottom: '1px'
                    }}>
                        {companyName || 'Company'}
                    </div>

                    {/* Main H1 */}
                    {appliedProduct ? (
                        <h1 style={{
                            fontSize: '22px',
                            fontWeight: 700,
                            margin: 0,
                            color: '#111',
                            lineHeight: 1.25
                        }}>
                            {buildMainTitle()}
                        </h1>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '4px 0' }}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
                                <path d="M10 2L3 7v6l7 5 7-5V7l-7-5z" stroke="#8896b3" strokeWidth="1.4" fill="none"/>
                                <circle cx="10" cy="10" r="2" fill="#8896b3"/>
                            </svg>
                            <span style={{ fontSize: '15px', color: '#8896b3', fontWeight: 500 }}>
                                Select a product and click Apply to view sentiment data
                            </span>
                        </div>
                    )}

                    {/* Context Line - lighter gray */}
                    {appliedProduct && (
                        <div style={{
                            fontSize: '12px',
                            color: '#999',
                            lineHeight: 1.3,
                            marginTop: '2px'
                        }}>
                            {buildSubline()}
                        </div>
                    )}
                </div>

                {/* Filter Bar */}
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: `${CONTROL_GAP}px`,
                    flexWrap: 'wrap',
                    rowGap: '12px'
                }}>
                    {/* Product */}
                    <div style={{ flex: '1 1 auto', minWidth: 100, maxWidth: 180, boxSizing: 'border-box' }}>
                        <div style={filterLabelStyle}>Product</div>
                        <ProductSelector
                            topicMap={filteredTopicMap}
                            selectedId={selectedProduct || ''}
                            onSelect={setSelectedProduct}
                            label=""
                            size="small"
                            sx={selectStyle}
                        />
                    </div>

                    {/* Quality Metric */}
                    <div style={{ flex: '1 1 auto', minWidth: 120, maxWidth: 200 }}>
                        <div style={filterLabelStyle}>Quality Metric</div>
                        <FormControl size="small" sx={selectStyle}>
                            <Select
                                value={selectedType}
                                onChange={handleTypeChange}
                                displayEmpty
                            >
                                {availableTypes.map(type => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    {/* Sources */}
                    <div style={{ flex: '1 1 auto', minWidth: 110, maxWidth: 180 }}>
                        <div style={filterLabelStyle}>Sources</div>
                        <FormControl size="small" sx={selectStyle}>
                            <Select
                                multiple
                                value={selectedSources}
                                onChange={handleSourceChange}
                                renderValue={(selected) => {
                                    if (selected.includes('all')) return 'All Sources';
                                    const labels = availableSources
                                        .filter(s => selected.includes(s.value))
                                        .map(s => s.label);
                                    return labels.length > 2
                                        ? `${labels.length} selected`
                                        : labels.join(', ');
                                }}
                            >
                                {availableSources.map((source) => (
                                    <MenuItem key={source.value} value={source.value}>
                                        <Checkbox
                                            checked={selectedSources.indexOf(source.value) > -1}
                                            size="small"
                                        />
                                        <ListItemText primary={source.label} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    {/* Competitors (only when the effective topic has any) */}
                    {competitorOptions.length > 0 && (
                        <div style={{ flex: '1 1 auto', minWidth: 120, maxWidth: 190 }}>
                            <div style={filterLabelStyle}>Competitors</div>
                            <FormControl size="small" sx={selectStyle}>
                                <Select
                                    multiple
                                    displayEmpty
                                    value={selectedCompetitors}
                                    onChange={handleCompetitorChange}
                                    renderValue={(selected) => {
                                        if (selected.length === 0) return 'None';
                                        const names = competitorOptions
                                            .filter(c => selected.includes(c.id))
                                            .map(c => c.name);
                                        return names.length > 2
                                            ? `${names.length} selected`
                                            : names.join(', ');
                                    }}
                                >
                                    {competitorOptions.map((comp) => (
                                        <MenuItem key={comp.id} value={comp.id}>
                                            <Checkbox
                                                checked={selectedCompetitors.indexOf(comp.id) > -1}
                                                size="small"
                                            />
                                            <ListItemText primary={comp.name} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                    )}

                    {/* Time Range */}
                    <div style={{ flex: '1 1 auto', minWidth: 90, maxWidth: 140 }}>
                        <div style={filterLabelStyle}>Time Range</div>
                        <FormControl size="small" sx={selectStyle}>
                            <Select
                                value={timeRange}
                                onChange={(e) => setTimeRange(e.target.value)}
                            >
                                {TIME_RANGE_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    {/* End Date */}
                    <div style={{ flex: '1 1 auto', minWidth: 120, maxWidth: 160 }}>
                        <div style={filterLabelStyle}>End Date</div>
                        <TextField
                            type="date"
                            size="small"
                            value={selectedEndDate}
                            onChange={(e) => setSelectedEndDate(e.target.value)}
                            InputProps={{
                                sx: {
                                    height: CONTROL_HEIGHT,
                                    fontSize: '13px',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#d0d0d0',
                                        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#1042c1',
                                    }
                                }
                            }}
                            placeholder="No limit"
                        />
                    </div>

                    {/* Stock overlay (publicly-traded companies only): absolute
                        price, or a relative-performance comparison vs peers /
                        S&P 500 / sector. */}
                    {companyTicker && (
                        <div style={{ flex: '0 0 auto', alignSelf: 'flex-end', minWidth: 150 }}>
                            <div style={filterLabelStyle}>Overlay</div>
                            <FormControl size="small" sx={selectStyle}>
                                <Select
                                    value={overlayMode}
                                    onChange={(e) => setOverlayMode(e.target.value)}
                                >
                                    <MenuItem value="off">Off</MenuItem>
                                    {PREMIUM_MODES.map((m) => (
                                        <MenuItem key={m.value} value={m.value}>
                                            {m.value === 'price' ? `Price (${companyTicker})` : m.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                    )}

                    {/* Apply Button */}
                    <div style={{ flex: '0 0 auto' }}>
                        <div style={{ ...filterLabelStyle, visibility: 'hidden' }}>&nbsp;</div>
                        <Button
                            variant="contained"
                            onClick={applyFilters}
                            sx={{
                                height: CONTROL_HEIGHT,
                                minWidth: isFiltersDirty ? '120px' : '72px',
                                padding: '6px 16px',
                                fontSize: '13px',
                                fontWeight: 600,
                                textTransform: 'none',
                                backgroundColor: isFiltersDirty ? '#1042c1' : '#1976d2',
                                boxShadow: 'none',
                                animation: isFiltersDirty ? 'pulseRing 1.5s ease-out infinite' : 'none',
                                transition: 'background-color 0.15s ease, box-shadow 0.15s ease, min-width 0.2s ease',
                                '&:hover': {
                                    boxShadow: isFiltersDirty
                                        ? '0 2px 8px rgba(16, 66, 193, 0.4)'
                                        : '0 2px 6px rgba(25, 118, 210, 0.3)',
                                    backgroundColor: isFiltersDirty ? '#0d38a3' : '#1565c0'
                                }
                            }}
                        >
                            {isFiltersDirty ? 'Apply Changes' : 'Apply'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Chart Section */}
            <div style={{ marginTop: '8px' }}>
                {appliedProduct && (
                    <SentimentAnalysisChart
                        key={`${appliedProduct}-${appliedType}-${appliedSources.join(',')}-${appliedTimeRange}-${appliedEndDate}-${appliedCompetitors.map(c => c.id).join('|')}`}
                        topicId={appliedProduct}
                        type={appliedType}
                        customTitle={null}
                        productName={null}
                        allTopicIds={appliedProduct === 'all' ? Object.keys(topicMap).filter(id => id !== 'all') : null}
                        timeRange={appliedTimeRange}
                        appliedSources={appliedSources}
                        hideHeader={true}
                        endDate={appliedEndDate || null}
                        companyTicker={companyTicker}
                        overlayMode={overlayMode}
                        competitors={appliedCompetitors.length > 0 ? appliedCompetitors : null}
                        mainSeriesName={appliedProduct === 'all' ? (companyName || 'All Topics') : getAppliedProductName()}
                    />
                )}
            </div>
        </div>
    );
};

export default CustomSentimentBreakdown;
